import { QualityManager } from './quality/QualityManager'
import type { QualityLevel, VideoSourceConfig } from './types'

export interface PlayerCoreEvents {
  onTimeUpdate: (currentTime: number, duration: number, buffered: number) => void
  onPlay: () => void
  onPause: () => void
  onEnded: () => void
  onWaiting: () => void
  onPlaying: () => void
  onVolumeChange: (volume: number, muted: boolean) => void
  onRateChange: (rate: number) => void
  onFullscreenChange: (fullscreen: boolean) => void
  onPipChange: (pip: boolean) => void
  onQualityChange: (levels: QualityLevel[], activeId: string, label: string) => void
  onError: (message: string) => void
}

export class PlayerCore {
  readonly video: HTMLVideoElement
  readonly container: HTMLElement
  readonly quality: QualityManager
  private events: PlayerCoreEvents
  private disposed = false

  constructor(video: HTMLVideoElement, container: HTMLElement, events: PlayerCoreEvents) {
    this.video = video
    this.container = container
    this.events = events
    this.quality = new QualityManager(video, events.onQualityChange)
    this.quality.setErrorHandler((msg) => this.events.onError(msg))
    this.bind()
  }

  private bind() {
    const v = this.video
    v.addEventListener('timeupdate', this.handleTime)
    v.addEventListener('durationchange', this.handleTime)
    v.addEventListener('progress', this.handleTime)
    v.addEventListener('play', () => this.events.onPlay())
    v.addEventListener('pause', () => this.events.onPause())
    v.addEventListener('ended', () => this.events.onEnded())
    v.addEventListener('waiting', () => this.events.onWaiting())
    v.addEventListener('playing', () => this.events.onPlaying())
    v.addEventListener('error', () => {
      const err = v.error
      this.events.onError(err ? `Media error ${err.code}` : 'Playback error')
    })
    v.addEventListener('volumechange', () =>
      this.events.onVolumeChange(v.volume, v.muted),
    )
    v.addEventListener('ratechange', () => this.events.onRateChange(v.playbackRate))
    document.addEventListener('fullscreenchange', this.handleFs)
    v.addEventListener('enterpictureinpicture', () => this.events.onPipChange(true))
    v.addEventListener('leavepictureinpicture', () => this.events.onPipChange(false))
  }

  private handleTime = () => {
    const v = this.video
    let buffered = 0
    if (v.buffered.length > 0) {
      buffered = v.buffered.end(v.buffered.length - 1)
    }
    this.events.onTimeUpdate(v.currentTime, v.duration || 0, buffered)
  }

  private handleFs = () => {
    this.events.onFullscreenChange(document.fullscreenElement === this.container)
  }

  dispose() {
    if (this.disposed) return
    this.disposed = true
    this.video.removeEventListener('timeupdate', this.handleTime)
    this.video.removeEventListener('durationchange', this.handleTime)
    this.video.removeEventListener('progress', this.handleTime)
    document.removeEventListener('fullscreenchange', this.handleFs)
    this.quality.destroy()
  }

  async loadContent(config: VideoSourceConfig, startTime = 0, autoplay = false) {
    await this.quality.loadContent(config, startTime, autoplay)
  }

  async loadAd(src: string) {
    await this.quality.loadPlainSrc(src, 0, true)
  }

  async resumeContent(time: number, autoplay = true) {
    await this.quality.resumeContent(time, autoplay)
  }

  play() {
    return this.video.play()
  }

  pause() {
    this.video.pause()
  }

  togglePlay() {
    if (this.video.paused) return this.play()
    this.pause()
    return Promise.resolve()
  }

  seek(time: number) {
    if (!Number.isFinite(time)) return
    this.video.currentTime = Math.max(0, Math.min(time, this.video.duration || time))
  }

  setVolume(volume: number) {
    this.video.volume = Math.max(0, Math.min(1, volume))
    if (volume > 0) this.video.muted = false
  }

  setMuted(muted: boolean) {
    this.video.muted = muted
  }

  toggleMute() {
    this.video.muted = !this.video.muted
  }

  setPlaybackRate(rate: number) {
    this.video.playbackRate = rate
  }

  async toggleFullscreen() {
    if (document.fullscreenElement === this.container) {
      await document.exitFullscreen()
    } else {
      await this.container.requestFullscreen()
    }
  }

  async togglePip() {
    if (!document.pictureInPictureEnabled) return
    if (document.pictureInPictureElement) {
      await document.exitPictureInPicture()
    } else {
      await this.video.requestPictureInPicture()
    }
  }

  setCaptions(on: boolean) {
    const tracks = this.video.textTracks
    for (let i = 0; i < tracks.length; i++) {
      tracks[i].mode = on ? 'showing' : 'hidden'
    }
  }
}
